<template>
  <v-row no-gutters>
    <v-col cols="12" v-if="mapPanelDataFetched && isFetched">
      <card-title-row
        v-if="showCountAttributeNameAsTitle"
        is-subtitle
        :straightTitle="countAttributeVerboseName"
        :has-icon-column="false"
      />
      <sidebar-summary :items="summary" />
    </v-col>
    <main-loader v-else />
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import MainLoader from '@/components/MainLoader';
import SidebarSummary from '@/components/SidebarSummary';

export default {
  name: 'SidebarSummaryCounts',
  components: { CardTitleRow, MainLoader, SidebarSummary },
  props: {
    layerId: {
      type: Number,
      required: true,
    },
    countsAttributeName: {
      type: String,
      required: true,
    },
    addLayerToProject: {
      type: Boolean,
      default: false,
    },
    showCountAttributeNameAsTitle: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    layer() {
      return this.layers?.[this.layerId] || undefined;
    },
    layerFilterExpression() {
      return this.layer?.filter_expression || null;
    },
    dataSourceName() {
      return this.layer?.data_source_name || '';
    },
    dataSource() {
      return this.dataSources?.[this.dataSourceName] || undefined;
    },
    dataSourceAttributes() {
      return this.dataSource?.attributes_schema.attributes || [];
    },
    countAttributeMetadata() {
      return this.dataSourceAttributes.find(attribute => attribute.name === this.countsAttributeName) || undefined;
    },
    countAttributeVerboseName() {
      return this.countAttributeMetadata?.verbose_name || '';
    },
    countAttributeDictValues() {
      return this.countAttributeMetadata?.allowed_values || [];
    },
    countAttributeDictColors() {
      return this.countAttributeMetadata?.dict_colors || [];
    },
    countAttributeColors() {
      const countAttributeColors = {};
      this.countAttributeDictValues.forEach((value, idx) => {
        countAttributeColors[value] = this.countAttributeDictColors[idx] || this.$_colors.primary;
      });
      return countAttributeColors;
    },

    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
  },
  data: () => ({
    isFetched: false,
    summary: [],
  }),
  methods: {
    getDataSourceAttributeUniqueValues: call('layers/getDataSourceAttributeUniqueValues'),
    addCountsLayerToProject() {
      if (!this.mapPanelDataFetched || this.projectLayers.map(layer => layer.id).includes(this.layerId)) return;
      const layer = JSON.parse(JSON.stringify(this.layer));
      if (layer) {
        layer.labels_visible = layer.labels_visible || false;
        layer.style = layer.style_web;
        delete layer.style_web;
        this.$store.set('layers/ADD_PROJECT_LAYERS!', [
          { layer_id: layer.id, visible: true, opacity: 1, has_permission: true, ...layer },
        ]);
        this.$root.$emit('pushProjectLayers', [
          { layer_id: layer.id, visible: true, opacity: 1, has_permission: true, ...layer },
        ]);
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.layerAddedToProject', { name: layer.name }),
        });
      }
    },
    async init() {
      try {
        if (this.addLayerToProject) {
          this.addCountsLayerToProject();
        }
        const r = await this.getDataSourceAttributeUniqueValues({
          dataSourceName: this.dataSourceName,
          attributeName: this.countsAttributeName,
          params: {
            features_filter: this.layerFilterExpression,
          },
        });
        const counts = r.values;
        const mapCounts = Object.keys(counts).map(value => {
          return { value, count: counts[value] };
        });
        const summary = mapCounts.map(element => {
          return {
            ...element,
            color: this.countAttributeColors[element.value] || this.$_colors.primary,
          };
        });
        this.summary = summary.map(element => {
          const { color, value, count } = element;
          return {
            straightText: value,
            color,
            titleCols: 5,
            titleClasses: ['pr-4'],
            rowClasses: ['pt-4'],
            value: count,
          };
        });
      } finally {
        this.isFetched = true;
      }
    },
  },
  watch: {
    mapPanelDataFetched: {
      handler(nV) {
        if (nV) this.init();
      },
      immediate: true,
    },
  },
};
</script>
